import {useEffect} from 'react'
import {toast} from 'react-toastify'
import {toastDefaultConfig, toastActionableConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils'
import type {
  NotificationPayload,
  NotificationSeverity,
  ValidationErrorPayload,
} from './handler-structures'

/**
 * The page's one listener for viewer messages that are addressed to the user rather than to a tab.
 *
 * Viewers answer a server action by posting its response to the host. When that response opens a
 * viewer the managers handle it, but `showNotification` and `actionValidationError` matched no
 * method on either manager (`handleMessageEvent` dispatches with `action in this`), so they were
 * dropped without a trace — a link the OBserver correctly reported as "desktop only" simply closed
 * its loading tab and said nothing.
 *
 * Mounted once, like {@code FilePickerHost}, and deliberately *not* folded into the managers:
 * several `GridViewerManager`s can be mounted at the same time, each filtering by `viewerType`, so
 * a handler there would raise one toast per manager.
 */
export function ViewerNotificationHost() {
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data
      // Anything can post to window: strings from browser extensions, nulls from dev tooling.
      if (typeof data !== 'object' || data === null) {
        return
      }

      switch ((data as {action?: unknown}).action) {
        case 'showNotification':
          showNotification((data as {value?: NotificationPayload}).value ?? {})
          return
        case 'actionValidationError':
          showValidationErrors((data as {value?: ValidationErrorPayload}).value ?? {})
          return
        default:
          return
      }
    }

    window.addEventListener('message', onMessage)

    return () => window.removeEventListener('message', onMessage)
  }, [])

  return null
}

/** Title and message are separate fields server-side; the toast is one line. */
function showNotification({severity, title, message}: NotificationPayload): void {
  const body = [title, message].filter(Boolean).join(': ')
  if (!body) {
    getLogger().warn('Ignoring an empty showNotification message')
    return
  }

  // An error is the one severity the user has to do something with — read it properly, usually copy
  // it — so it gets the longer, hover-pausable fuse. Anything else is just a notice.
  toastFor(severity)(body, severity === 'ERROR' ? toastActionableConfig : toastDefaultConfig)
}

function toastFor(severity: NotificationSeverity | undefined) {
  switch (severity) {
    case 'SUCCESS':
      return toast.success
    case 'WARNING':
      return toast.warning
    case 'ERROR':
      return toast.error
    default:
      return toast.info
  }
}

/**
 * Last resort for rejected form values. A viewer that still has the wizard open intercepts these
 * itself and shows each message against its field; anything that reaches here has no form left, so
 * one toast carrying every message is the only place left to put them.
 */
function showValidationErrors({errors}: ValidationErrorPayload): void {
  const body = (errors ?? [])
    .map((error) => error?.message)
    .filter(Boolean)
    .join('\n')

  toast.error(body || 'The values were rejected by the server.', toastActionableConfig)
}
